import { CARDS_PAGE } from "@highmarch/app";
import { dealsLink } from "../ui/deals/dealsLink";
import { openDeals } from "../ui/deals/dealsSheet";
import { claimAllCards } from "../ui/deals/cardsActions";
import { CARDS_CHIP_ICON, CardChip, cardChip, chipCard } from "../ui/deals/cardsModel";
import { fmtCountdown } from "../ui/deals/dealsModel";
import { uiUrl } from "../ui/portraits";
import { toast } from "../ui/toast";
import type { Feature } from "./registry";
import "../ui/styles/cards.css";

/**
 * Cards in the hub (MP-01/02): while a card runs (or a banked day waits), a chip under the Deals button names its
 * day ("Day 4/30") and turns gold — CLAIM — when a day is waiting. A tap claims every card's waiting days and shows the
 * free-claim receipt; otherwise it opens Deals → Cards. A claim, never an offer: the chip shows only for a card the
 * player already owns, and like every hub control it never appears in a level or on the end screen. The rules are
 * @highmarch/app `Cards` (the catalog's Deals); the War Chest Fund's badge rides on the Deals pill (Deals.badge).
 */

/** Top-bar order: right after the Deals button (30). */
const CARDS_PILL_ORDER = 31;

function chipHtml(c: CardChip): string {
  return `<span class="cardsbtn${c.ready ? " ready" : ""}"><img decoding="sync" src="${uiUrl(CARDS_CHIP_ICON)}" alt=""><b class="disp">${c.label}</b>${c.count > 0 ? `<i class="dot">${c.count}</i>` : ""}</span>`;
}

export const cardsFeature: Feature = {
  id: "cards",
  register(ctx, registry) {
    const cards = () => ctx.catalog.deals.cards;
    const chip = (): CardChip | null => cardChip(cards().views(ctx.now()));

    registry.topBarPill({
      id: "cards", order: CARDS_PILL_ORDER,
      html: () => { const c = chip(); return c ? chipHtml(c) : ""; },
      onTap: (hub, pill) => {
        const l = dealsLink();
        if (!l) return;
        l.hub = hub;
        if (claimAllCards(l, hub.parent, pill)) return;
        const v = chipCard(cards().views(ctx.now()));
        if (!openDeals({ page: CARDS_PAGE }) && v) toast(hub.parent, `Claimed today. Next in ${fmtCountdown(v.nextAt - ctx.now())}`);
      },
    });
    // The hub's tick: today is noted on each visit (a clock moved back later reopens nothing, MP-13), and the chip
    // follows the UTC day (a new day turns it gold without a hub redraw); it reads its name aloud.
    registry.hubWidget({
      id: "cards",
      mount: (hub) => {
        if (cards().noteVisit(ctx.now())) ctx.persist();
        let shown = "";
        const update = () => {
          const el = hub.parent.querySelector<HTMLElement>('.hub .fpill[data-pill="cards"]');
          const c = chip();
          if (!el) return;
          const html = c ? chipHtml(c) : "";
          if (html !== shown && el.innerHTML !== html) el.innerHTML = html;
          shown = html;
          if (c) el.setAttribute("aria-label", c.aria);
        };
        update();
        return { update, dispose: () => undefined };
      },
    });
    if (ctx.debug) {
      (window as unknown as { __tdCards: unknown }).__tdCards = {
        cards: cards,
        /** Captures: moves a running card's start back `days` days (as if bought then). */
        age: (offerId: string, days: number) => { const r = cards().state.run(offerId); if (r) { r.start -= days; r.claimedThrough -= days; ctx.persist(); } },
      };
    }
  },
};
